import './screens.css';
import { el, setText, text } from './walkout.js';

/**
 * The warming-up screen (step 96): what a player sees when they have chosen
 * faster than the fighters arrived.
 *
 * The title and the create screen are up before the game has loaded, which is
 * the point — a slow line used to show nothing at all for twenty seconds. So a
 * player can finish creating a fighter while the bodies are still on the
 * wire, and something has to stand between "start climbing" and a hub that
 * cannot be built yet. This is it: it says what it is waiting for, goes away
 * by itself the moment the game is ready, and — if the game cannot load at
 * all — says so and offers the one thing that can help, a reload. Never a
 * blank screen, either way.
 */
export function createGate({ container = document.body, reload = () => window.location.reload() } = {}) {
  const root = el('div', 'screen screen--gate');
  root.hidden = true;
  const kind = text('div', 'screen__kind', 'warming up');
  const line = text('div', 'screen__how', '');
  const detail = text('div', 'screen__hint', '');
  const retry = el('button', 'screen__go');
  retry.textContent = 'try again';
  retry.hidden = true;
  retry.addEventListener('click', () => reload());
  root.append(kind, line, detail, retry);
  container.append(root);

  let up = false;
  let started = 0;
  let timer = null;

  function tick() {
    const seconds = Math.round((performance.now() - started) / 1000);
    setText(detail, seconds >= 3 ? `${seconds} s — on a slow line the fighters take a while` : '');
  }

  return {
    element: root,
    get up() {
      return up;
    },
    /** Up, saying what it is waiting for. */
    wait(what = 'the fighters are still arriving') {
      root.dataset.failed = 'false';
      setText(kind, 'warming up');
      setText(line, what);
      retry.hidden = true;
      root.hidden = false;
      up = true;
      started = performance.now();
      tick();
      clearInterval(timer);
      timer = setInterval(tick, 1000);
    },
    /** The game could not be loaded. Says why, and offers a reload. */
    fail(reason) {
      clearInterval(timer);
      root.dataset.failed = 'true';
      setText(kind, 'could not load the fight');
      setText(line, reason || 'the connection dropped before the game arrived');
      setText(detail, 'your career is saved; nothing has been lost');
      retry.hidden = false;
      root.hidden = false;
      up = true;
    },
    hide() {
      clearInterval(timer);
      root.hidden = true;
      up = false;
    },
  };
}
